import React, { useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from './ui/button';
import { Pencil, Eraser, RotateCcw, Save, Keyboard } from 'lucide-react';

const DrawingPad = ({ onSave, existingDrawing = null, label = "Drawing" }) => {
  const sigCanvas = useRef();
  const [penColor, setPenColor] = useState('#000000');
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [textInput, setTextInput] = useState('');

  const clear = () => {
    sigCanvas.current.clear();
  };

  const undo = () => {
    const data = sigCanvas.current.toData();
    if (data && data.length > 0) {
      data.pop();
      sigCanvas.current.fromData(data);
    }
  };

  const save = () => {
    if (sigCanvas.current.isEmpty() && !textInput) {
      return;
    }
    
    const dataURL = sigCanvas.current.isEmpty() ? null : sigCanvas.current.toDataURL();
    onSave({
      drawing: dataURL,
      text: textInput
    });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-slate-700">{label}</label>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant={showKeyboard ? "default" : "outline"}
            size="sm"
            onClick={() => setShowKeyboard(!showKeyboard)}
          >
            <Keyboard className="w-4 h-4 mr-1" />
            {showKeyboard ? 'Hide Keyboard' : 'Show Keyboard'}
          </Button>
        </div>
      </div>

      {/* Keyboard Input */}
      {showKeyboard && (
        <textarea
          value={textInput}
          onChange={(e) => setTextInput(e.target.value)}
          className="w-full min-h-24 p-3 border border-slate-300 rounded-lg resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          placeholder="Type your notes here..."
        />
      )}

      {/* Drawing Canvas */}
      <div className="border-2 border-purple-200 rounded-lg bg-white">
        <div className="p-2 border-b border-purple-200 flex items-center gap-2">
          {/* Color Picker */}
          <div className="flex items-center gap-1">
            {['#000000', '#0000FF', '#FF0000', '#00FF00'].map(color => (
              <button
                key={color}
                type="button"
                onClick={() => setPenColor(color)}
                className={`w-8 h-8 rounded border-2 ${
                  penColor === color ? 'border-purple-500 ring-2 ring-purple-200' : 'border-slate-300'
                }`}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
          
          <div className="flex-1" />
          
          {/* Tools */}
          <Button type="button" variant="outline" size="sm" onClick={undo}>
            <RotateCcw className="w-4 h-4" />
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={clear}>
            <Eraser className="w-4 h-4" />
          </Button>
          <Button type="button" className="bg-purple-500 hover:bg-purple-600" size="sm" onClick={save}>
            <Save className="w-4 h-4 mr-1" />
            Save
          </Button>
        </div>
        
        <SignatureCanvas
          ref={sigCanvas}
          canvasProps={{
            className: 'w-full h-48 cursor-crosshair',
            style: { touchAction: 'none' }
          }}
          penColor={penColor}
          minWidth={1}
          maxWidth={3}
        />
      </div>

      {existingDrawing && (
        <div className="p-3 bg-slate-50 rounded-lg">
          <p className="text-xs text-slate-500 mb-2">Previously saved:</p>
          <img src={existingDrawing} alt="Saved drawing" className="border border-slate-200 rounded" />
        </div>
      )}
    </div>
  );
};

export default DrawingPad;
